<template>
    <main id="compare-machine-comparison">
        <div class="wrapper">
            <div class="top-part">
                <h1 class="title-with-icon">
                    <span class="icon-compare" :class="brandColor"></span>
                    {{ $t("i_compare") }}
                </h1>

                <div class="temp-subtitle" :class="brandColor" v-if="competitors.length === 0">
                    {{ $t('comparison_results') }}
                </div>
                <template v-if="competitors.length > 0">
                    <div class="tabs" v-if="!isPhone">
                      <span
                          v-for="tab in tabs"
                          :key="tab.code"
                          class="tab"
                          :class="{ active: selectedTab === tab.code }"
                          :data-tracking="tab.code === 'compare' ? 'view_product_compare_page' : ''"
                          @click="switchTab(tab.code)"
                      >
                          {{ tab.label }}
                      </span>
                    </div>
                    <b-select
                      v-else
                      v-model="selectedTab"
                      class="select-tab"
                      size="is-large"
                      rounded
                      expanded
                      @input="switchTab($event)"
                  >
                    <option
                        v-for="tab in tabs"
                        :key="tab.code"
                        :value="tab.code"
                        :data-tracking="tab.code === 'compare' ? 'view_product_compare_page' : ''"
                    >
                      {{ tab.label }}
                    </option>
                  </b-select>
                </template>

                <div class="machines-comparison-container" :class="'has-'+baseMachinesDatas.length+'-columns'" v-if="comparisonIsShowable">
                    <div
                        class="machines-comparison"
                        v-for="(comparisonData, comparisonDataIndex) in comparisonDatas"
                        :key="comparisonData.label"

                    >
                        <div class="label-column">
                            <div class="label-column-wrap">
                                <div class="cell-title" :class="{'with-large-top-margin': comparisonDataIndex === 0}">
                                    <div class="group-features-label-container">
                                    <span
                                        class="action-plus-minus-button"
                                        :class="{'plus': columnIsFolded(comparisonDataIndex), 'minus': !columnIsFolded(comparisonDataIndex)}"
                                        @click="toggleColumn(comparisonDataIndex)"
                                        aria-label="fold/unfold"
                                    ></span>
                                        <h3 class="labels-title" :class="brandColor">{{ comparisonData.label }}</h3>
                                    </div>
                                </div>
                                <div
                                    class="cell"
                                    :class="{'folded': columnIsFolded(comparisonDataIndex)}"
                                    v-for="value in comparisonData.features[0].values"
                                    :key="value.label"
                                >
                                    <span class="label-text">{{ value.label }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="slider-column">
                            <div class="mobile-slider-controls">
                                <button
                                    class="action previous "
                                    :class="{available: canGoPrevious()}"
                                    :aria-label="$t('previous')"
                                    @click.prevent="slidePrev()"
                                ></button>
                                <button
                                    class="action next "
                                    :class="{available: canGoNext()}"
                                    :aria-label="$t('next')"
                                    @click.prevent="slideNext()"
                                ></button>
                            </div>

                            <hooper
                                :ref="'category_'+comparisonDataIndex+'_slider'"
                                :itemsToShow="itemToShow"
                                :itemsToSlide="1"
                                :mouseDrag="false"
                                :touchDrag="isPhone"
                                :wheelControl="false"
                                @slide="slideChanged($event)"
                                :key="itemToShow"

                            >
                                <slide v-for="(featureColumn, featureIndex) in comparisonData.features" :key="featureColumn.machineId">
                                    <div class="features-column">
                                        <div class="hide-trigger-container" v-if="comparisonDataIndex === 0">
                                            <b-checkbox type="is-brand" :size="isPhone ? null : 'is-medium'" v-model="baseMachinesDatas[featureIndex].isShowed"></b-checkbox>
                                            <div class="machine-number" v-html="featureIndex === 0 ? $t('reference_machine_br') : $tc('machine_number', featureIndex)"></div>
                                        </div>
                                        <div class="features-column-wrapper">
                                            <div class="cell-title" :class="{'hidden': !baseMachinesDatas[featureIndex].isShowed}">
                                            <span class="model-name">
                                              {{ getMachineDatas(featureColumn.machineId)['Model'] }} {{ getMachineDatas(featureColumn.machineId)['Tier'] }}
                                            </span>
                                                <span class="brand-name">
                                                {{ getMachineDatas(featureColumn.machineId)['Manufacturer'] }}
                                            </span>
                                            </div>
                                            <div
                                                class="cell"
                                                :class="[{'folded': columnIsFolded(comparisonDataIndex)}, {'hidden': !baseMachinesDatas[featureIndex].isShowed}, {'with-media': isArray(featureValue.value)}]"
                                                v-for="featureValue in featureColumn.values"
                                                :key="featureValue.label"
                                            >
                                                <template v-if="isArray(featureValue.value)">
                                                    <p
                                                        v-for="(media, index) in mediaListFiltered(featureValue.value)"
                                                        :key="media['Filename']"
                                                        v-if="media['Description'] === 'Chart'"
                                                        class="media-link-container"
                                                    >
                                                        <a
                                                            :href="'https://www.speccheck.com/machine_files/'+media['Filename']"
                                                            class="media-link"
                                                            :class="brandColor"
                                                            :data-mediabox="'media_'+comparisonDataIndex+'_'+featureIndex"
                                                            @click="enableBlur()"
                                                        >
                                                            <span class="icon-image"></span>

                                                            <span class="link-text">
                                                            {{ $tc('see_the_abacus', mediaListFiltered(featureValue.value).length > 1 ? (index+1) : null) }}
                                                        </span>
                                                        </a>
                                                    </p>
                                                </template>
                                                <template v-else>
                                                    {{ featureValue.value }}
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </slide>
                            </hooper>
                        </div>
                    </div>
                </div>

                <div class="machines-competitors-container" :class="'has-'+competitors.length+'-columns'" v-else-if="competitors.length > 0">
                  <div class="machines-comparison">

                    <div class="slider-column">
                      <div class="mobile-slider-controls">
                        <button
                            class="action previous "
                            :class="{available: canGoPrevious()}"
                            :aria-label="$t('previous')"
                            @click.prevent="slidePrev()"
                        ></button>
                        <button
                            class="action next "
                            :class="{available: canGoNext()}"
                            :aria-label="$t('next')"
                            @click.prevent="slideNext()"
                        ></button>
                      </div>
                      <hooper
                          v-if="!isPhone && scrollY > 120"
                          :ref="'competitor_slider_heading'"
                          :itemsToShow="itemToShow"
                          :itemsToSlide="1"
                          :mouseDrag="false"
                          :touchDrag="false"
                          :wheelControl="false"
                          class="competitors-title-scroll"
                          :class="{'centered': itemToShow > competitors.length}">
                          <slide v-for="(competitor, competitorIndex) in competitors"
                                 :key="competitor.id" class="heading">
                            <h2>{{ competitor.name }}</h2>
                          </slide>
                      </hooper>

                      <hooper
                          :ref="'competitor_slider'"
                          :itemsToShow="itemToShow"
                          :itemsToSlide="1"
                          :mouseDrag="true"
                          :touchDrag="isPhone"
                          :wheelControl="false"
                          @slide="slideChanged($event)"
                          :key="itemToShow"
                          class="args-hooper"
                          :class="{'centered': itemToShow > competitors.length}"
                      >
                        <slide
                            v-for="(competitor, competitorIndex) in competitors"
                            :key="competitor.id">
                          <div class="competitor-slide">
                            <h2 v-if="isPhone || scrollY < 120" class="is-color-red">{{ competitor.name }}</h2>
                            <div class="block-argument">
                              <h3>{{ $t("arguments") }}</h3>
                              <ul>
                                <li v-for="arg in competitor.arguments" :key="arg.id">{{ arg.values.text }}</li>
                              </ul>
                            </div>
                            <div class="block-counter-argument">
                              <h3>{{ $t("counter_arguments") }}</h3>
                              <ul>
                                <li v-for="counter in competitor.objections" :key="counter.id">{{ counter.values.text }}</li>
                              </ul>
                            </div>
                          </div>
                        </slide>
                      </hooper>
                    </div>
                  </div>
                </div>

                <div class="new-comparison-btn-bottom-banner" v-if="competitors.length === 0">
                    <router-link
                        :to="{name: 'compare-selection'}"
                        tag="button"
                        class="button is-primary is-rounded is-uppercase with-small-side-padding "
                        :class="[brandBackgroundColor, {'is-large': !isPhone}, {'is-small': isPhone}]"
                    >
                        <span>{{ $t('new_comparison') }}</span>
                        <span class="icon-arrow-right is-right-side"></span>
                    </router-link>

                    <a class="trigger-bottom-configuration" @click="revealNav()">
                        <img src="@/assets/img/languette.svg" alt="">

                    </a>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import {mapState} from 'vuex'

    import { Hooper, Slide } from 'hooper';
    import '@/../node_modules/hooper/dist/hooper.css';

    import machineService from "@/services/speccheck/machine";
    import compareMixins from "@/common/mixins/compare";
    import stickyMixins from "@/common/mixins/sticky";

    export default {
        name: 'CompareMachinesComparison',
        mixins: [
          compareMixins,
          stickyMixins
        ],
        props: {
          competitors: {
            type: Array,
            default: () => []
          }
        },
        components: {
            Hooper,
            Slide,
        },
        data() {
            return {
                tabs: [
                    {
                        code: "counterArguments",
                        label: this.$t('counter_arguments')
                    },
                    {
                        code: "compare",
                        label: this.$t('compare_with_other_products')
                    },
                ],
                currentSlide: 0,
                selectedTab: 'compare',
                baseMachinesDatas: null,
                comparisonDatas: null,
                itemToShow:2,
                Win:window,
            }
        },
        computed: {
            ...mapState({
                isPhone: state => state.settings.isPhone,
                isTablet: state => state.settings.isTablet,
                isPortrait: state => state.settings.isPortrait,
                machinesToCompare: state => state.compare.machinesToCompare
            }),
            comparisonIsShowable(){
                return (this.selectedTab === 'compare' && this.baseMachinesDatas && this.comparisonDatas);
            },
            getEnv(){
              return process.env.VUE_APP_ENV;
            },
        },
        watch: {
          currentSlide() {
            if (this.competitors.length > 0) {
              // console.log(this.$refs['competitor_slider_heading']);
              this.$refs['competitor_slider_heading'].slideTo(this.currentSlide);
            } else {
              this.comparisonDatas.forEach((comparisonData, index) => {
                this.$refs['category_' + index + '_slider'][0].slideTo(this.currentSlide);
              })
            }

          },
          isTablet: function () {
            this.changeItemToShow();
          },
          isPortrait: function () {
            this.changeItemToShow();
          },
          isPhone: function () {
            this.changeItemToShow();
          },

        },
        created(){
            if (this.machinesToCompare.length > 0) {
              this.startLoading();
              machineService.machineDatasComparison({
                mids: this.machinesToCompare.join(','),
                med: 1,
                uom: 1,
                locale: this.$store.state.settings.locale,
                unitType: this.$store.state.settings.unitType
              })
                  .then((response) => {
                    let speckcheckResponseDatas = response.data.data;

                    let baseMachinesDatas = [];
                    let comparisonDatas = [];
                    let comparisonDatasCurrentIndex = 0;
                    speckcheckResponseDatas.forEach(speckcheckResponseData => {
                      // Si DataType = I, on est dans le listing des machines avec leur ID et leur index,
                      // -> on push la base des infos machines (ID et INDEX dans le tableau) dans notre tableau final
                      if (speckcheckResponseData['DataType'] === 'I') {
                        this.machinesToCompare.forEach(machineToCompareId => {
                          let machineArrayIndex = speckcheckResponseData['Data'].findIndex(x => x['Machine_ID'] === machineToCompareId);
                          let machineDatas = speckcheckResponseData['Data'][machineArrayIndex];
                          machineDatas.isShowed = true;
                          machineDatas.specCheckIndex = machineArrayIndex;
                          baseMachinesDatas.push(machineDatas)
                        })

                        this.baseMachinesDatas = baseMachinesDatas;

                      }

                          // Si DataType = S, on le label d'un nouveau groupe de valeurs
                      // -> on push ce nouveau groupe dans notre tableau final et on prepare le terrain pour accueillir les valeurs
                      else if (speckcheckResponseData['DataType'] === 'S') {
                        comparisonDatas.push({
                          label: this.$_.capitalize(this.$_.lowerCase(speckcheckResponseData['Label'])),
                          slideIndex: 0,
                          isShowed: true,
                          isFolded: false,
                          features: this.baseMachinesDatas.map(x => {
                            return {
                              machineId: x['Machine_ID'],
                              values: []
                            }
                          })
                        })
                        comparisonDatasCurrentIndex = (comparisonDatas.length - 1);
                      }
                          // Pour les autres DataType, on est dans le cas de valeurs
                      // -> on push ces valeurs dans notre tableau final on fonction de leur type
                      else {
                        var indexComparison = 0;
                        this.baseMachinesDatas.forEach(baseMachineDatas => {
                          //let tempMachineIndex = comparisonDatas[comparisonDatasCurrentIndex].features.findIndex(x => x.machineId === baseMachineDatas['Machine_ID']);
                          let tempMachineIndex = baseMachineDatas['specCheckIndex'];

                          let value = Object.values(speckcheckResponseData['Data'][tempMachineIndex])[0];
                          if (value.length > 0) {
                            if (speckcheckResponseData['Unit'] !== undefined)
                              value += ' ' + speckcheckResponseData['Unit']
                          } else {
                            value = '/';
                          }

                          comparisonDatas[comparisonDatasCurrentIndex].features[indexComparison].values.push({
                            label: speckcheckResponseData['Label'],
                            value: value
                          })
                          indexComparison++;
                        })
                      }
                    })
                    //console.log(comparisonDatas);
                    this.comparisonDatas = comparisonDatas;

                    this.changeItemToShow(this.baseMachinesDatas);
                    setTimeout(() => {
                      this.$root.wamediabox.bindAll(document.querySelector("#compare-machine-comparison"));
                    }, 500);
                  })
                  .finally(() => this.stopLoading());
            } else if(this.competitors.length > 0) {
                this.selectedTab = 'counterArguments';
                this.changeItemToShow();
            }
        },

        methods: {
            mediaListFiltered(medias) {
                return medias.filter(media => media['Description'] === 'Chart')
            },
            getMachineDatas(machineId) {
                return this.baseMachinesDatas.find(x => x['Machine_ID'] === machineId)
            },
            slidePrev() {
                if(this.canGoPrevious())
                    this.currentSlide--;
            },
            slideNext() {
                if(this.canGoNext())
                    this.currentSlide++;
            },
            canGoPrevious() {
                return this.currentSlide > 0;
            },
            canGoNext() {
              if (this.competitors.length > 0) {
                if (this.isPhone) {
                  let slideVisible = this.currentSlide*this.itemToShow;
                  return ((slideVisible+1) < this.competitors.length);
                } else {
                  return ((this.currentSlide+1) < this.competitors.length);
                }
              } else {
                if (this.isPhone) {
                  let slideVisible = this.currentSlide*this.itemToShow;
                  return ((slideVisible+1) < this.baseMachinesDatas.length);
                } else {
                  return ((this.currentSlide+1) < this.baseMachinesDatas.length);
                }
              }
            },
            slideChanged(payload) {
                this.currentSlide = payload.currentSlide;
            },
            toggleColumn(index) {
                this.comparisonDatas[index].isFolded = !this.columnIsFolded(index);
            },
            columnIsFolded(index){
                return this.comparisonDatas[index].isFolded;
            },
            revealNav() {
              let content = document.getElementById("compare-machine-comparison");
              let nav = document.getElementById("mobile-bottom-navigation");
              content.classList.toggle("menu-reveal");
              nav.classList.toggle("reveal");
            },
            changeItemToShow() {
              console.log("window size changed");
              if (this.competitors.length > 0) {
                if(this.isTablet && !this.isPortrait)
                  this.itemToShow=3.1;
                else if (this.isPhone && this.isPortrait )
                  this.itemToShow=1.1;
                else if(this.isPhone && !this.isTablet && !this.isPortrait)
                  this.itemToShow=2.1;
                else
                  this.itemToShow=4.1;

              } else {
                if(this.isTablet && !this.isPortrait)
                  this.itemToShow=this.baseMachinesDatas.length;
                else if (this.isPhone && this.isPortrait )
                  this.itemToShow=1.1;
                else if(this.isPhone && !this.isTablet && !this.isPortrait)
                  this.itemToShow=2.1;
                else
                  this.itemToShow=this.baseMachinesDatas.length;
              }
              //console.log('Portrait :'+this.isPortrait);
              //console.log('Phone :'+this.isPhone);
              //console.log('Tablet :'+this.isTablet);
              //console.log('Items :'+this.itemToShow);
            },
            switchTab(code) {
              console.log(code)
              this.selectedTab = code;
              if (code === 'compare' && this.machinesToCompare.length === 0) this.$emit('switch');
            }
        }
    }
</script>

<style lang="scss">
    $new-comparison-btn-bottom-banner-height: 92px;
    $new-comparison-btn-bottom-banner-mobile-height: 110px;
    $new-comparison-btn-bottom-banner-mobile-height-offset: 0;

    #compare-machine-comparison {
        position: relative;
        padding-bottom: $new-comparison-btn-bottom-banner-height;

        @media #{$mq_phone}  {
            padding-bottom: $new-comparison-btn-bottom-banner-mobile-height-offset;

            &.menu-reveal {
                padding-bottom: $new-comparison-btn-bottom-banner-mobile-height;

                .new-comparison-btn-bottom-banner {
                    align-items: center;
                    height: 110px;
                    bottom: $mobile-bottom-navigation;

                    button {
                        margin-top: 10px;
                    }

                    .display-menu:after {
                        -webkit-transform: unset;
                        -moz-transform: unset;
                        -ms-transform: unset;
                        -o-transform: unset;
                        transform: unset;
                    }
                }
            }
        }

        .top-part {
            padding-top: 60px;
            text-align: center;

            @media #{$mq_phone} {
                padding-top: 10px;
            }

            .temp-subtitle {
                margin-top: 45px;
                font-size: 23px;
                font-weight: 900;
                line-height: 0.96;

                @media #{$mq_phone} {
                    font-size: 20px;
                    margin-top: 60px;
                }
            }

            .tabs {
                margin-top: 45px;
            }

            .select-tab {
                margin: 20px 0 50px 0;

                @media #{$mq_phone} {
                  margin-bottom: 15px;
                }

                .select select {
                    font-size: 13px;
                }
            }
        }

        .machines-comparison-container {
            position: relative;

            &.has-4-columns {
                &:before {
                    left: calc((100% - (100% - 230px)) + ((100% - 230px) / 4));
                }

                .machines-comparison {
                    .label-column {
                        .label-column-wrap {
                            .cell {
                                &:after {
                                    width: calc(((100vw - 30px) - ((100vw - 30px) / 8)));
                                }
                            }
                        }
                    }
                }
            }

            &.has-3-columns {
                &:before {
                    left: calc((100% - (100% - 230px)) + ((100% - 230px) / 3));
                }

                .machines-comparison {
                    .label-column {
                        .label-column-wrap {
                            .cell {
                                &:after {
                                    width: calc(((100vw - 30px) - ((100vw - 30px) / 6)));
                                }
                            }
                        }
                    }
                }
            }

            &.has-2-columns {
                &:before {
                    left: calc((100% - (100% - 230px)) + ((100% - 230px) / 2));
                }

                .machines-comparison {
                    .label-column {
                        .label-column-wrap {
                            .cell {
                                &:after {
                                    width: calc(((100vw - 30px) - ((100vw - 30px) / 4)));
                                }
                            }
                        }
                    }
                }
            }

            &:before {
                content: '';
                display: block;
                position: absolute;
                top: 100px;
                bottom: 0;
                z-index: 0;
                width: 2px;
                background-image: url('~@/assets/img/spaced-dot-vertical.svg');
                background-repeat: repeat-y;
                background-size: 2px 7px;

                @media #{$mq_phone} {
                    display: none;
                }
            }

            .machines-comparison {
                display: flex;
                margin-top: 35px;
                margin-bottom: 50px;
                text-align: left;

                @media #{$mq_phone} {
                    margin-bottom: 25px;
                }

                &:first-child {
                    .slider-column {
                        .mobile-slider-controls {
                            top: 130px;
                        }
                    }
                }

                .label-column {
                    flex: 0 0 230px;

                    @media #{$mq_phone} {
                        flex: 0 0 160px;
                        margin-left: -20px;
                    }

                    .label-column-wrap {
                        position: relative;

                        @media #{$mq_phone} {
                            margin-right: 20px;
                            padding: 0 10px;
                            box-shadow: 20px 0 34px 0 rgba(0, 0, 0, 0.05);

                            &:before {
                                content: '';
                                position: absolute;
                                top: -30px;
                                left: 0;
                                right: 0;
                                height: 30px;
                                z-index: 0;
                                background-color: $white;
                            }

                            &:after {
                                content: '';
                                position: absolute;
                                bottom: -30px;
                                left: 0;
                                right: 0;
                                height: 30px;
                                z-index: 0;
                                background-color: $white;
                            }
                        }


                        .cell-title {
                            position: relative;
                            display: flex;
                            height: 90px;
                            padding-bottom: 30px;
                            align-items: flex-end;
                            z-index: 1;

                            @media #{$mq_phone} {
                                height: calc(90px + 40px);
                            }

                            &.with-large-top-margin {
                                height: calc(90px + 100px);

                                @media #{$mq_phone} {
                                    height: calc(90px + 170px);
                                }
                            }

                            &:after {
                                display: none;
                            }

                            &.small {
                                height: 50px;
                            }

                            .group-features-label-container {
                                display: flex;
                                align-items: center;

                                .action-plus-minus-button {
                                    @media #{$mq_phone} {
                                        display: none;
                                    }
                                }

                                .labels-title {
                                    @include medium-title;
                                    margin-left: 10px;

                                    @media #{$mq_phone} {
                                        font-size: 13px;
                                        text-transform: uppercase;
                                        margin-left: 0;
                                    }
                                }
                            }
                        }

                        .cell {
                            position: relative;
                            display: flex;
                            align-items: center;
                            height: 60px;

                            @media #{$mq_phone} {
                                height: 42px;
                            }

                            &.folded {
                                display: none;
                            }

                            &:after {
                                content: '';
                                position: absolute;
                                top: 50%;
                                left: 0;
                                z-index: 0;
                                height: 2px;
                                background-image: url('~@/assets/img/spaced-dot.svg');
                                background-position: center center;
                                background-repeat: repeat-x;
                                background-size: 7px 2px;

                                @media #{$mq_phone} {
                                    width: 60vw;
                                }
                            }

                            .label-text {
                                position: relative;
                                z-index: 1;
                                font-size: 15px;
                                background-color: $white;

                                @media #{$mq_phone} {
                                    font-size: 14px;
                                }
                            }
                        }
                    }
                }

                .slider-column {
                    position: relative;
                    flex: 1 1 auto;
                    overflow-x: hidden;

                    @media #{$mq_phone} {
                        padding-top: 40px;
                    }

                    .mobile-slider-controls {
                        display: none;
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: calc(100vw - 170px);
                        z-index: 5;

                        @media #{$mq_phone} {
                            display: block;
                        }

                        .action {
                            opacity: 0.2;
                            cursor: pointer;


                            &:after {
                                @include icomoon;
                                content: "\e918";
                                display: inline-block;
                                font-size: 22px;
                                color: $blackish;
                                transition: color 0.3s ease;
                            }

                            &.available {
                                opacity: 1;
                            }

                            &.previous {
                                float: left;

                                &:after {
                                    transform: rotate(90deg);
                                }
                            }

                            &.next {
                                float: right;
                                margin-right: 25px;

                                &:after {
                                    transform: rotate(-90deg);
                                }
                            }
                        }
                    }


                    .hooper {
                        height: auto;

                        .hooper-slide {
                            height: auto;
                            text-align: center;

                            //@media screen and (max-width: 767px) and (orientation: landscape) {
                            //    width: calc(90% / 2)!important;
                            //}
                        }

                    }

                    .features-column {
                        margin: 0 auto;
                        padding: 0 20px;
                        max-width: 250px;
                        text-align: center;

                        @media #{$mq_phone} {
                            margin: 0;
                            padding: 0;
                          max-width: 100%;
                        }

                        .hide-trigger-container {
                            height: 100px;
                            padding-top: 10px;

                            @media #{$mq_phone} {
                                height: 130px;
                            }

                            .machine-number {
                                margin-top: 5px;
                                line-height: 1.2;
                                font-size: 18px;
                                font-weight: 700;
                                color: $medium-grey;

                                @media #{$mq_phone} {
                                    font-size: 15px;
                                }
                            }
                        }

                        .features-column-wrapper {
                            background-color: $light-grey;
                            border-radius: 20px;
                            margin: 0 5px;

                            .cell-title {
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                flex-direction: column;
                                height: 90px;
                                padding: 0 5px;
                                border-bottom: 2px solid $white;

                                &.hidden {
                                    .model-name, .brand-name {
                                        opacity: 0;
                                    }
                                }

                                .model-name {
                                    display: block;
                                    margin-bottom: 5px;
                                    font-weight: 700;
                                    opacity: 1;
                                    transition: opacity 0.3s ease;

                                    @media #{$mq_phone} {
                                        font-size: 14px;
                                    }
                                }

                                .brand-name {
                                    color: $medium-grey;
                                    opacity: 1;
                                    transition: opacity 0.3s ease;

                                    @media #{$mq_phone} {
                                        font-size: 14px;
                                    }
                                }

                                &.small {
                                    height: 50px;
                                }
                            }

                            .cell {
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                height: 60px;
                                padding: 0 5px;
                                opacity: 1;
                                transition: opacity 0.3s ease;

                                @media #{$mq_phone} {
                                    font-size: 14px;
                                    height: 42px;
                                }

                                &.hidden {
                                    opacity: 0;
                                }

                                &.folded {
                                    display: none;
                                }

                                &.with-media {
                                    flex-direction: column;
                                }

                                .media-link-container {
                                    margin: 3px 0;

                                    .media-link {
                                        [class^="icon-"], [class*=" icon-"] {
                                            @media #{$mq_phone} {
                                                display: none;
                                            }
                                        }

                                        .link-text {
                                            display: inline-block;
                                            margin-left: 5px;
                                            text-decoration: underline;

                                            @media #{$mq_phone} {
                                                margin-left: 0;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        .new-comparison-btn-bottom-banner {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 10;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: $new-comparison-btn-bottom-banner-height;
            background-color: $light-grey;
            transition: ease-in 0.37s;

            @media #{$mq_phone} {
                height: $new-comparison-btn-bottom-banner-mobile-height;
                bottom: 0;
                flex-direction: column;

                button {
                    margin-top: 10px;
                }
            }

          .trigger-bottom-configuration {
            display:block;
            position:relative;
            text-align:center;
            color:#FFF;
            width:100%;
            height:25px;
            margin-top:10px;
            img {transform: rotate(-180deg);}
            &:after {
              content:'Menu';
              position:absolute;
              width:100%;
              left:0;
              top:15px;
              color:#ffffff;
              text-transform:uppercase;

            }
            @media #{$mq_phone_min} {
              display: none;
            }

          }
            .display-menu {
                margin-top: 5px;
                background: #000;
                color: #fff;


                @media #{$mq_phone_min} {
                    display: none;
                }

                &:after {
                    font-family: "icomoon";
                    speak: none;
                    font-style: normal;
                    font-weight: normal;
                    font-variant: normal;
                    text-transform: none;
                    line-height: 1;
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                    content: "\E918";
                    display: inline-block;
                    font-size: 16px;
                    color: #fff;
                    -webkit-transition: color 0.37s ease-in;
                    transition: color 0.37s ease;
                    margin-left: 10px;
                    transform: rotate(180deg);
                }
            }
        }

        .competitors-title-scroll {
          height: 60px;
          position: fixed;
          left: 0;
          top: 145px;
          z-index: 1;

          &.centered .hooper-track {
            justify-content: center;
          }

          @media #{$mq_phone} {
            display: none;
          }

          .heading {
            background-color: $brand-red;
            color: $white;
          }

          .hooper-slide {
            flex: 1;
          }

          h2 {
            font-size: 23px;
            font-weight: 700;
            letter-spacing: 1.1px;
            line-height: 0.96;
            margin-top: 20px;
            margin-bottom: 5px;
            //height: 50px;
            white-space: nowrap;

            @media #{$mq_phone} {
              white-space: normal;
            }
          }
        }

        .args-hooper {
          margin: 24px auto;
          text-align: left;
          height: auto;

          &.centered .hooper-track {
            justify-content: center;
          }

          .hooper-slide {
            flex: 1;
          }

          .competitor-slide {
            padding: 34px 32px;
            border-right: solid 5px #f1f1f1;

            @media #{$mq_phone} {
              padding: 16px 18px;
            }

            .block-argument {
              margin-top: 20px;
              margin-bottom: 15px;
              padding: 10px 18px 2px 30px;
              border-radius: 8px;
              background: #f5f5f5;

            }

            .block-counter-argument {
              margin-top: 20px;
              margin-bottom: 15px;
              padding: 10px 18px 2px 30px;
              border-radius: 8px;
              box-shadow: 0 24px 38px 3px rgba(0, 0, 0, 0.14), 0 9px 46px 8px rgba(0, 0, 0, 0.12), 0 11px 15px -7px rgba(0, 0, 0, 0.20);
            }

            h2 {
              font-size: 23px;
              font-weight: 700;
              letter-spacing: 1.1px;
              line-height: 0.96;
              margin-top: 20px;
              margin-bottom: 5px;
              //height: 50px;
              white-space: nowrap;

              @media #{$mq_phone} {
                white-space: normal;
              }
            }

            h3 {
              font-size: 18px;
              font-weight: bold;
              letter-spacing: 1.1px;
              text-transform: uppercase;
              margin-top: 14px;
              margin-left: -15px;
              margin-bottom: 5px;
            }

            ul {
              list-style: initial;

              @media #{$mq_phone} {
                padding-left: 4px;
              }
            }

            ul > li {
              font-size: 15px;
              font-weight: normal;
              font-stretch: normal;
              font-style: normal;
              line-height: normal;
              letter-spacing: normal;
              color: #000000;
              margin: 20px auto;
            }
          }
        }
    }


    #mobile-bottom-navigation.collapsed {

        @media #{$mq_phone} {
            height: 0;
          opacity:1;
            transition: ease-in 0.37s;
            pointer-events: none;
          background:#101010;

            /*&:before {
                content: '\25BE';
                position: absolute;
                transition: ease-in 0.4s;
                transform: scaleY(-1);
                border-radius: 50%;
                top: -20px;
                height: 50px;
                width: 50px;
                background: #000;
                color: $brand-red;
                font-size: 35px;
                display: flex;
                align-items: flex-end;
                justify-content: center;
                pointer-events: all;
                z-index: 500;
            }*/

            a {
                opacity: 0;
                transition: ease-in 0.3s;
            }

            &.reveal {
                height: $mobile-bottom-navigation;
                pointer-events: auto;

                &:before {
                    pointer-events: all;
                    z-index: 500;
                    transform: none;
                    align-items: center;
                }

                a {
                    opacity: 1;
                }
            }
        }
    }
</style>

